/**
 * Pure timing rules for the session token. No storage, no clock, no env — every input is
 * a parameter so the decision is unit-testable and cannot differ between a developer's
 * machine and CI (see the `process.env` gotcha in CLAUDE.md).
 */

import jwtDecode from 'jwt-decode';

export interface ITokenTiming {
	/** Access-token issue time, Unix seconds. */
	iat: number;
	/** Access-token expiry, Unix seconds. */
	exp: number;
	/**
	 * Refresh-token expiry, Unix seconds — or `null` when the refresh token does not decode
	 * as a JWT. `null` means UNKNOWN, never "expired": the backend contract does not promise
	 * a JWT here, and treating an opaque token as dead would end every session on first refresh.
	 */
	refreshExp: number | null;
}

/** Floor and ceiling on how far ahead of expiry the refresh fires. */
export const MIN_LEAD_MS = 60_000;
export const MAX_LEAD_MS = 300_000;
/** Fraction of the token's lifetime reserved as lead. */
export const LEAD_FRACTION = 0.2;

const decodeExp = (jwt: string): { exp?: number; iat?: number } | null => {
	try {
		const claims = jwtDecode<{ exp?: number; iat?: number }>(jwt);
		return claims && typeof claims === 'object' ? claims : null;
	} catch {
		return null;
	}
};

/**
 * Timing off the stored pair. `null` when the access token carries no usable `exp`, which
 * is the same as "not logged in" for scheduling purposes.
 */
export const readTokenTiming = (token: string | undefined, refreshToken: string | undefined): ITokenTiming | null => {
	if (!token) return null;
	const access = decodeExp(token);
	if (!access || typeof access.exp !== 'number' || !Number.isFinite(access.exp)) return null;

	const iat = typeof access.iat === 'number' && Number.isFinite(access.iat) && access.iat < access.exp ? access.iat : null;
	const refresh = refreshToken ? decodeExp(refreshToken) : null;
	const refreshExp = refresh && typeof refresh.exp === 'number' && Number.isFinite(refresh.exp) ? refresh.exp : null;

	return {
		// A token with no `iat` is treated as if it were minted an hour before it expires —
		// the lifetime the backend actually issues — so the lead stays sensible.
		iat: iat ?? access.exp - 3600,
		exp: access.exp,
		refreshExp,
	};
};

/** Lead time in ms: 20 % of the lifetime, clamped to [60 s, 5 min]. */
export const refreshLeadMs = (iatSec: number, expSec: number) => {
	const lifetimeMs = Math.max(0, (expSec - iatSec) * 1000);
	return Math.min(MAX_LEAD_MS, Math.max(MIN_LEAD_MS, Math.round(lifetimeMs * LEAD_FRACTION)));
};

/** The instant (ms) at which a refresh becomes due. */
export const refreshDueAtMs = (timing: ITokenTiming) => timing.exp * 1000 - refreshLeadMs(timing.iat, timing.exp);

export type RefreshPlan =
	/** Due and the user is active: call extend-session now. */
	| { kind: 'refresh' }
	/** Not yet due: sleep until `dueAtMs`. */
	| { kind: 'wait'; dueAtMs: number }
	/** Due (or past expiry) but the user is idle: the idle flow owns what happens next. */
	| { kind: 'idle' }
	/** The refresh token itself is known to be expired: no call can succeed. */
	| { kind: 'dead' };

export interface IPlanRefreshInput {
	nowMs: number;
	timing: ITokenTiming;
	msSinceActivity: number;
	idleWindowMs: number;
}

/**
 * The whole scheduling decision in one place.
 *
 * Order matters: a dead refresh token is reported before anything else because no amount
 * of activity can rescue it; then the lead window; then activity. Note that an ACTIVE user
 * whose access token has already expired still gets `refresh` — extend-session takes the
 * refresh token, not the access token, so a tab that slept through its due time recovers
 * the moment it wakes.
 */
export const planRefresh = ({ nowMs, timing, msSinceActivity, idleWindowMs }: IPlanRefreshInput): RefreshPlan => {
	if (timing.refreshExp !== null && timing.refreshExp * 1000 <= nowMs) return { kind: 'dead' };

	const dueAtMs = refreshDueAtMs(timing);
	if (nowMs < dueAtMs) return { kind: 'wait', dueAtMs };

	return msSinceActivity < idleWindowMs ? { kind: 'refresh' } : { kind: 'idle' };
};

export const isAccessExpired = (timing: ITokenTiming, nowMs: number) => timing.exp * 1000 <= nowMs;
